/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import {
  Box,
  FormControl,
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import moment from 'moment';
import { toNumber } from 'lodash';
import { useDispatch } from 'react-redux';
import TableLayout from '../../../Components/Ui/TableLayout';
import { HeadCell } from '../../../Objects/interfaces/TableTypes';
import { useTailwindBrand } from '../../../Components/shared';
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from '../../../config/environment.config';
import { BpRadio, capitalizeFirstLetter, fullNameChild } from '../../../Utils/Function';
import Functions from '../../../Helpers/Functions';
import ClientRepository from '../../../Repositories/ClientRepository';
import { RESET_COURSE_DECLARATION_INFO } from '../../../Redux/Reducers/DeclarationCourseReducers';
import { SHOW_ERROR, SHOW_WARNING } from '../../../Redux/Reducers/MessageReducers';
import { TdpDescEnum, TdpEnum } from '../../../Enum/TdpEnum';
import Teachr from 'src/Objects/models/Teachr';
import { VT_MONTHLY_CEILING_TTC, isVerifiedTrainer, vtTtcFromNet } from '../../../Utils/VerifiedTrainerAmounts';
import MonthlyQuotaCell from '../../../Components/App/Clients/MonthlyQuotaCell';

interface ClientRadioListProps {
  childs: any[];
  value: string;
  nameSearch: string;
  onSearchChange: (value: string) => void;
  totalItems: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  pageLimit: (newLimit: number) => void;
  setValue: (val: string) => void;
  handleClose: () => void;
  handleClickOpen: () => void;
  setChild: (child: any) => void;
  setDateTimeTdpMoment: (val: any) => void;
  setLoad: (val: boolean) => void;
  load: boolean;
  teachr: Teachr
}

export default function ClientRadioList({
  childs,
  value,
  nameSearch,
  onSearchChange,
  totalItems,
  currentPage,
  onPageChange,
  pageLimit,
  setValue,
  handleClose,
  handleClickOpen,
  setChild,
  setDateTimeTdpMoment,
  setLoad,
  load,
  teachr
}: ClientRadioListProps) {
  const dispatch = useDispatch();
  const { brandConfig, classes } = useTailwindBrand();
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const lastMonthName = capitalizeFirstLetter(moment().subtract(1, 'month').locale('fr').format('MMMM'));
  const defaultFees = parseFloat(teachr?.defaultFees ?? '0') || 0;
  const netMultiplier = 1 - defaultFees;
  // Verified Trainer : l'API renvoie des montants nets (70 %), affichés en prix total TTC avec un plafond de 1980 €
  const isVt = isVerifiedTrainer();
  const toDisplayAmount = (value: any): number => (isVt ? vtTtcFromNet(value, teachr) : parseFloat(value));
  const maxNet = isVt ? VT_MONTHLY_CEILING_TTC : 1980 * netMultiplier;

  // ── Head cells ──────────────────────────────────────────────────────────
  const headCells: HeadCell[] = [
    { id: 0, label: '', disable: false, numeric: false },
    { id: 1, label: capitalizeFirstLetter(getActiveClientBrandConfig()), disable: false, numeric: false },
    {
      id: 2,
      disable: false,
      numeric: false,
      align: 'center',
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          Heures déclarées
          <Tooltip title={`Format : heures ce mois vs heures en ${lastMonthName}`} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help' }} />
          </Tooltip>
        </span>
      ),
    },
    {
      id: 3,
      disable: false,
      numeric: false,
      align: 'center',
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          📅 {capitalizeFirstLetter(getActivePrestationBrandConfig())} ce mois
          <Tooltip title="Le nombre maximum de cours déclarables par élève sur un mois calendaire est de 8." arrow>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }} />
          </Tooltip>
        </span>
      ),
    },
    {
      id: 4,
      disable: false,
      numeric: false,
      align: 'center',
      label: (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          💰 Montant
          <Tooltip title={isVt
            ? `Prix total TTC déclaré sur le mois en cours (payé par le client + crédit d'impôt). Plafond : ${maxNet.toFixed(0)}€ par client et par mois.`
            : `Montant déclaré sur le mois en cours. Le plafond mensuel est de Plafond : ${maxNet.toFixed(0)}€ / mois inclus.`} arrow>
            <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary', cursor: 'help', verticalAlign: 'middle' }} />
          </Tooltip>
        </span>
      ),
    },
  ];

  const resetAndReopen = () => {
    handleClose();
    setValue('');
    dispatch({ type: RESET_COURSE_DECLARATION_INFO, payload: null });
    setChild(undefined);
  };

  const handleTdpStatusOk = (child: any) => {
    const date = moment();
    const dateTimeTdpMoment = moment(child?.activationDate, 'YYYY/MM/DD HH:mm:ss');
    const secondTime = toNumber(dateTimeTdpMoment.format('ss'));
    const dateSetSecond = date.set('second', secondTime);
    setDateTimeTdpMoment(dateTimeTdpMoment);

    if (date.format('DD/MM/YYYY') === dateTimeTdpMoment.format('DD/MM/YYYY')) {
      if (date.diff(dateTimeTdpMoment, 'hours', true) <= 2) {
        const diffMinutes = dateSetSecond.diff(dateTimeTdpMoment, 'minutes');
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        const remaining = `${String(1 - hours).padStart(2, '0')}h${String(minutes === 0 && (1 - hours) > 0 ? 0 : 60 - minutes).padStart(2, '0')}`;
        resetAndReopen();
        handleClose();
        dispatch({ type: SHOW_WARNING, payload: `Vous ne pouvez pas déclarer de cours pour ${fullNameChild(child?.childFirstName, child?.childLastName)} actuellement, car son compte a été validé à ${dateTimeTdpMoment.format('HH')}h${dateTimeTdpMoment.format('mm')}. Veuillez attendre et réessayer dans ${remaining}.` });
        // handleClickOpen();
      } else {
        setChild(child);
      }
    } else {
      setChild(child);
    }
  };

  const handleAccountBlocked = (child: any, error: any) => {
    resetAndReopen();
    const msgs: Record<string, string> = {
      [TdpEnum.STATUS_3_NF]: TdpDescEnum.STATUS_3_NF,
      [TdpEnum.STATUS_1_BLOQUE]: TdpDescEnum.STATUS_1_BLOQUE,
      [TdpEnum.STATUS_2_EC]: TdpDescEnum.STATUS_2_EC,
      [TdpEnum.STATUS_5_MANDAT_ECHU]: TdpDescEnum.STATUS_5_MANDAT_ECHU,
    };
    const html = msgs[error.code] ?? `Le compte de ${fullNameChild(child?.childFirstName, child?.childLastName)} a été bloqué. Vous ne pouvez plus déclarer de cours pour cet élève pour l'instant.`;
    dispatch({ type: SHOW_ERROR, payload: html });
    // handleClickOpen();
  };

  const handleRowClick = (child: any) => {
    if (load) return;
    const isTeachrContact = child?.isTeachrContact;
    const firstCondition = !isTeachrContact && (!child?.pricePerHour || !child?.feesPerHour);

    if (firstCondition) return;

    setValue(String(child.childId));
    setLoad(true);
    if (!teachr?.id) return
    ClientRepository.refreshStatusTdp(child?.clientId, teachr?.id)
      .then((res) => {
        if (res) {
          if (firstCondition) {
            resetAndReopen();
            dispatch({ type: SHOW_WARNING, payload: `Cet ajout est en attente de validation par un administrateur. Veuillez patienter environ deux jours ou contactez le service client.` });
            // handleClickOpen();
          } else if (res?.etat === 'OK') {
            handleTdpStatusOk(child);
          } else {
            handleAccountBlocked(child, res);
          }
        }
      })
      .catch((error) => {
        resetAndReopen();
        const msg = error.message === 'Invalid Authorization' ? error.message : 'Une erreur inconnue, veuillez contacter le service client';
        dispatch({ type: SHOW_ERROR, payload: msg });
      })
      .finally(() => setLoad(false));
  };
  // ── Rendu d'une ligne ────────────────────────────────────────────────────
  const renderRow = (child: any) => {
    const isTeachrContact = child?.isTeachrContact;
    const firstCondition = !isTeachrContact && (!child?.pricePerHour || !child?.feesPerHour);
    const fullName = fullNameChild(child?.childFirstName, child?.childLastName);
    const duration = child?.duration === 0 ? '0h' : (Functions.renderDuration(child?.duration) || '0h');
    const durationlastmonth = child?.durationlastmonth === 0 ? '0h' : (Functions.renderDuration(child?.durationlastmonth) || '0h');
    const isSelected = value === String(child.childId);
    return (
      <TableRow
        key={child.childId}
        hover={!firstCondition}
        onClick={() => !firstCondition && handleRowClick(child)}
        sx={{
          cursor: firstCondition ? 'not-allowed' : 'pointer',
          opacity: firstCondition ? 0.45 : 1,
          bgcolor: isSelected ? 'action.selected' : undefined,
          '&:nth-of-type(odd)': { bgcolor: isSelected ? 'action.selected' : 'action.hover' },
        }}
      >
        {/* Radio */}
        <TableCell padding="checkbox" sx={{ pl: 1.5 }}>
          <Tooltip title={firstCondition ? TdpDescEnum.STATUS_6_ACTIVE_DEFAULT : ''}>
            <span>
              <BpRadio
                className={classes.primaryText}
                checked={isSelected}
                value={String(child.childId)}
                disabled={firstCondition}
                onChange={() => { }}
              />
            </span>
          </Tooltip>
        </TableCell>
        {/* Nom */}
        <TableCell>
          <Typography
            variant="body2"
            fontWeight={isSelected ? 700 : 400}
            sx={{ fontFamily: brandConfig.fontFamily }}
          >
            {fullName}
          </Typography>
        </TableCell>

        {/* Heures déclarées ce mois vs mois précédent */}
        <TableCell align="center">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" fontWeight={700} sx={{ fontFamily: brandConfig.fontFamily }}>
              {duration}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily, whiteSpace: 'nowrap' }}>
              vs {durationlastmonth} en {lastMonthName}
            </Typography>
          </Box>
        </TableCell>

        {/* Cours ce mois calendaire */}
        <TableCell align="center">
          {/* ce qu'il reste au client sur 8, tous profs confondus, et la part du prof connecté */}
          <MonthlyQuotaCell total={child?.coursesThisRollingMonth} mine={child?.coursesMonthByThisTeachr} />
        </TableCell>

        {/* Montant ce mois */}
        <TableCell align="center">
          {(() => {
            const netAmountNum = child?.amount != null ? toDisplayAmount(child.amount) : null;
            // const netAmount = rawAmount != null ? rawAmount * netMultiplier : null;
            const amountLastMonthStr = child?.amountlastmonth != null
              ? `${toDisplayAmount(child.amountlastmonth).toFixed(2)}€`
              : null;
           
            // Reste avant plafond : montant déjà déclaré sur le mois, tous coachs confondus
            const amountDeclared = child?.amountForTheMonth ? toDisplayAmount(child.amountForTheMonth) : 0;
            const remaining = Math.max(0, maxNet - amountDeclared);
            // const remaining = netAmount != null ? Math.max(0, maxNet - netAmount) : null;
            const amountColor = netAmountNum == null ? 'text.secondary'
              : netAmountNum >= 1980 ? 'error.main'
                : netAmountNum >= 1800 ? 'warning.main'
                  : 'text.primary';
            const subColor = netAmountNum != null && netAmountNum >= 1980 ? 'error.main'
              : netAmountNum != null && netAmountNum >= 1800 ? 'warning.main'
                : 'text.secondary';
            const subText = netAmountNum == null ? `Plafond : ${maxNet.toFixed(0)}€ / mois`
              : netAmountNum === 0 ? 'Aucun montant ce mois'
                : remaining! <= 0 ? 'Plafond atteint ce mois'
                  : remaining <= (isVt ? 400 : 400 * netMultiplier) ? `Plus que ${remaining.toFixed(0)}€ ce mois !`
                    : `${!isNaN(remaining) ? remaining!.toFixed(0) : 0}€ restants ce mois`;
            return (
              <Box>
                <Typography variant="body2" fontWeight={700} sx={{ fontFamily: brandConfig.fontFamily, color: amountColor }}>
                  {netAmountNum != null ? `${netAmountNum.toFixed(2)}€` : '-'}
                </Typography>
                <Typography variant="caption" display="block" sx={{ fontFamily: brandConfig.fontFamily, color: subColor, whiteSpace: 'nowrap' }}>
                  {subText}
                </Typography>
                {amountLastMonthStr != null && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: brandConfig.fontFamily, whiteSpace: 'nowrap' }}>
                    vs {amountLastMonthStr} en {lastMonthName}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </TableCell>
      </TableRow>
    );
  };

  // ── Pagination handlers ──────────────────────────────────────────────────
  const handleChangePage = (_: unknown, newPage: number) => onPageChange(newPage);
  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newLimit = parseInt(e.target.value, 10);
    setRowsPerPage(newLimit);
    pageLimit(newLimit);
  };

  // ── Rendu principal ──────────────────────────────────────────────────────
  return (
    <FormControl sx={{ width: '100%' }}>
      {/* ── Barre de recherche ── */}
      <Box sx={{ mb: 1.5 }}>
        <TextField
          fullWidth
          size="small"
          placeholder={`Filtrer par ${getActiveClientBrandConfig()} ou par mail`}
          value={nameSearch}
          onChange={(e) => { onSearchChange(e.target.value); onPageChange(0) }}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />,
            endAdornment: nameSearch ? (
              <IconButton size="small" onClick={() => onSearchChange('')}>
                <ClearIcon fontSize="small" />
              </IconButton>
            ) : null,
          }}
          sx={{ '& .MuiInputBase-root': { fontFamily: brandConfig.fontFamily } }}
        />
      </Box>

      {/* ── Tableau ── */}
      {childs.length > 0 ? (
        <TableLayout
          Children={childs.map((child) => renderRow(child))}
          headCells={headCells}
          page={currentPage + 1}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          rowsPerPage={rowsPerPage}
          count={totalItems}
          size="small"
          stickyHeader
          paperSx={{ boxShadow: 1, borderRadius: 2 }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 220,
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: 2,
            gap: 1,
            bgcolor: 'grey.50',
          }}
        >
          <Typography fontSize="2rem">👤</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: brandConfig.fontFamily }}>
            Aucun {getActiveClientBrandConfig()} trouvé
          </Typography>
        </Box>
      )}
    </FormControl>
  );
}
